import { useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useTrialPatients } from "@/hooks/useActiveTrials";
import { EnrollPatientDialog } from "@/components/trial-site/install/EnrollPatientDialog";
import { Loader2, Search, UserPlus, AlertTriangle, FileSignature } from "lucide-react";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { formatBirthYear } from "@/utils/dateUtils";

interface AssignPatientToTrialDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trialId: string;
  trialSiteId: string;
}

export function AssignPatientToTrialDialog({ 
  open, 
  onOpenChange, 
  trialId, 
  trialSiteId 
}: AssignPatientToTrialDialogProps) {
  const [searchQuery, setSearchQuery] = useState("");

  // Fetch site patients
  const { data: patients = [], isLoading } = useQuery({
    queryKey: ['site-patients-for-assignment', trialSiteId],
    queryFn: async () => {
      const { data, error } = await (supabase as any)
        .from('patient_trial_site_assignments')
        .select(`
          profile_id,
          profiles!patient_trial_site_assignments_profile_id_fkey!inner(
            id,
            patient_number,
            first_name,
            last_name,
            date_of_birth
          )
        `)
        .eq('trial_site_id', trialSiteId)
        .in('assignment_status', ['active', 'assigned'])
        .order('profiles(last_name)');

      if (error) throw error;
      return data || [];
    },
    enabled: open
  });

  // Fetch patient consents to check consent status
  const { data: patientConsents = [] } = useQuery({
    queryKey: ['patient-consents-for-assignment', trialSiteId],
    queryFn: async () => {
      // Use .or() filter to avoid TypeScript type instantiation depth issues
      const { data, error } = await supabase
        .from('patient_consents')
        .select('patient_id, status, consent_form_id')
        .eq('trial_site_id', trialSiteId)
        .or('status.eq.signed,status.eq.verified');

      if (error) throw error;
      return data || [];
    },
    enabled: open
  });

  // Create a set of patient IDs who have signed consent
  const consentedPatientIds = new Set(patientConsents.map((c: any) => c.patient_id));

  // Filter patients based on search
  const filteredPatients = patients.filter((p: any) => {
    if (!searchQuery) return true;
    const profile = p.profiles;
    const fullName = `${profile.first_name} ${profile.last_name}`.toLowerCase();
    const patientNumber = profile.patient_number?.toLowerCase() || '';
    return fullName.includes(searchQuery.toLowerCase()) || patientNumber.includes(searchQuery.toLowerCase());
  });

  // Same enroll step as the Matches tab, so every patient gets a visit schedule.
  const [enrollProfileId, setEnrollProfileId] = useState<string | null>(null);
  const { assignPatient, isAssigning } = useTrialPatients(trialId);
  const handleAssignPatient = (patientId: string) => setEnrollProfileId(patientId);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px]">
        <DialogHeader>
          <DialogTitle>Assign Patient to Trial</DialogTitle>
          <DialogDescription>
            Select a patient from your site roster to assign to this trial
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Search Input */}
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or patient number..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9"
            />
          </div>

          {/* Patient List */}
          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ) : filteredPatients.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              {searchQuery ? 'No patients found matching your search' : 'No active patients at this site'}
            </div>
          ) : (
            <ScrollArea className="h-[400px] pr-4">
              <div className="space-y-2">
                {filteredPatients.map((patient: any) => {
                  const profile = patient.profiles;
                  const hasConsent = consentedPatientIds.has(profile.id);
                  
                  return (
                    <div
                      key={profile.id}
                      className="flex items-center justify-between p-3 border rounded-lg hover:bg-accent transition-colors"
                    >
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <p className="font-medium">
                            {profile.first_name} {profile.last_name}
                          </p>
                          <Badge variant="outline" className="font-mono text-xs">
                            {profile.patient_number || 'No ID'}
                          </Badge>
                          {hasConsent ? (
                            <Badge variant="default" className="text-xs bg-green-600">
                              <FileSignature className="h-3 w-3 mr-1" />
                              Consented
                            </Badge>
                          ) : (
                            <Badge variant="secondary" className="text-xs">
                              No Consent
                            </Badge>
                          )}
                        </div>
                        {profile.date_of_birth && (
                          <p className="text-sm text-muted-foreground">
                            Year of birth: {formatBirthYear(profile.date_of_birth)}
                          </p>
                        )}
                        {!hasConsent && (
                          <p className="text-xs text-amber-600 dark:text-amber-400 mt-1 flex items-center gap-1">
                            <AlertTriangle className="h-3 w-3" />
                            eConsent not yet signed
                          </p>
                        )}
                      </div>
                      <Button
                        size="sm"
                        onClick={() => handleAssignPatient(profile.id)}
                        disabled={isAssigning}
                      >
                        {isAssigning ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <>
                            <UserPlus className="h-4 w-4 mr-1" />
                            Assign
                          </>
                        )}
                      </Button>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </div>
      </DialogContent>
      {enrollProfileId && (
        <EnrollPatientDialog
          open={!!enrollProfileId}
          onOpenChange={(o) => { if (!o) setEnrollProfileId(null); }}
          patientProfileId={enrollProfileId}
          trialSiteId={trialSiteId}
          trialId={trialId}
          croTrialId={trialId}
          onEnrolled={() => {
            // Also add to the trial patient list (status + milestone billing read it).
            assignPatient({ patient_id: enrollProfileId, trial_id: trialId, trial_site_id: trialSiteId, status: 'screening', skipVisitGeneration: true });
            setEnrollProfileId(null); onOpenChange(false); setSearchQuery("");
          }}
        />
      )}
    </Dialog>
  );
}
